import type { ZipcodesStatic, ZipInfo } from 'zipcodes';

/**
 * Distance estimates from ZIP codes, for when Google's route lookup can't answer. The zipcodes package is large
 * (about 860 KB compressed), so it loads on first use only.
 */

const EARTH_RADIUS_MILES = 3958.7613;
let zipcodesRuntimePromise: Promise<ZipcodesStatic> | null = null;

/**
 * Convert straight-line (Haversine) distance to an estimated road distance.
 * Studies show road routes are typically 1.2–1.4× the straight-line distance;
 * we use 1.3× as a reasonable middle ground.
 */
export const ROAD_DISTANCE_MULTIPLIER = 1.3;

export function loadZipcodesRuntime(): Promise<ZipcodesStatic> {
  if (!zipcodesRuntimePromise) {
    zipcodesRuntimePromise = import('zipcodes').then((module) => {
      return module.default ?? (module as unknown as ZipcodesStatic);
    });
  }
  return zipcodesRuntimePromise;
}

// The zipcodes package only ships US data, so we restrict sanitization to
// 5-digit ZIP or ZIP+4 inputs until international coverage is supported.
export const sanitizePostalCode = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^\d{5}(?:-\d{4})?$/u);
  if (!match) return null;
  const [primary] = match[0].split('-');
  return primary ?? null;
};

const toRadians = (value: number) => (value * Math.PI) / 180;

export const haversineDistanceMiles = (
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number => {
  const lat1 = toRadians(aLat);
  const lon1 = toRadians(aLon);
  const lat2 = toRadians(bLat);
  const lon2 = toRadians(bLon);
  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_MILES * c;
};

export function computeStraightLineMiles(
  zipcodes: ZipcodesStatic,
  warehouseZip: string,
  venueZip: string,
): number | null {
  const distance = zipcodes.distance(warehouseZip, venueZip);
  if (typeof distance === 'number' && Number.isFinite(distance) && distance >= 0) {
    return distance;
  }
  const a: ZipInfo | null = zipcodes.lookup(warehouseZip);
  const b: ZipInfo | null = zipcodes.lookup(venueZip);
  if (!a || !b) return null;
  const { latitude: aLat, longitude: aLon } = a;
  const { latitude: bLat, longitude: bLon } = b;
  const coords = [aLat, aLon, bLat, bLon];
  if (coords.some(value => typeof value !== 'number' || !Number.isFinite(value))) return null;
  return haversineDistanceMiles(aLat, aLon, bLat, bLon);
}

/** Estimated driving miles between two ZIP codes, or null when either ZIP is unknown. */
export async function estimateRoadMilesBetweenZips(
  fromPostalCode: string | null | undefined,
  toPostalCode: string | null | undefined,
): Promise<number | null> {
  const fromZip = sanitizePostalCode(fromPostalCode);
  const toZip = sanitizePostalCode(toPostalCode);
  if (!fromZip || !toZip) return null;
  const straightLineMiles = computeStraightLineMiles(await loadZipcodesRuntime(), fromZip, toZip);
  return straightLineMiles == null ? null : straightLineMiles * ROAD_DISTANCE_MULTIPLIER;
}
